<template>
  <v-row>
    <v-col cols="12">
      <v-row v-if="props.patientChart && props.patientChart.length" class="section-container">
        <v-col cols="12" class="header-with-select-box">
          <h3 class="text-primary weight-600">Supporting Documents</h3>
        </v-col>

        <v-col v-if="props.patientChart && props.patientChart.length" cols="12">
          <v-table class="">
            <thead>
              <tr>
                <th>Uploaded Documents</th>
                <th class="text-right"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(chart, chartIndex) in props.patientChart" :key="`${chart.label}-${chartIndex}`">
                <td>
                  <div class="">{{ chart.label }}</div>
                </td>
                <td>
                  <div v-if="chart.links && chart.links.length" class="d-flex align-center justify-end flex-wrap">
                    <template v-for="link in chart.links" :key="`prepared-${link}`">
                      <span v-if="captionFor(link)" class="text-caption text-medium-emphasis mr-2">
                        {{ captionFor(link) }}
                      </span>
                    </template>
                    <template v-if="props.isEditable">
                      <v-btn
                        v-for="link in chart.links"
                        :key="link"
                        target="_blank"
                        variant="text"
                        color="outline"
                        prepend-icon="mdi-delete"
                        @click="
                          deleteUploadedFile({
                            label: chart.label,
                            link: link,
                            storeVar: `patientChart`,
                            storeVarIndex: chartIndex,
                            chartEntry: chart,
                          })
                        ">
                        Delete
                      </v-btn>
                    </template>
                    <v-btn
                      v-for="link in chart.links"
                      :key="link"
                      variant="text"
                      color="primary"
                      prepend-icon="mdi-download"
                      @click="openDocument(props.orderKey, link)">
                      Download
                    </v-btn>
                  </div>
                  <div v-else class="d-flex justify-end">
                    <div v-if="!props.isEditable">Not Uploaded</div>
                    <div v-else>
                      <ChartUploadFileComponent
                        v-if="props.cmmBudgeted"
                        store-var="patientChart"
                        :label="chart.label"
                        :store-var-index="chartIndex"
                        :other-links="otherLinks"
                        @download-link="(info) => addDownloadLink(info, chart)" />
                      <UploadFileComponent
                        v-else
                        :download-destination="`patientChart`"
                        :download-label="chart.label"
                        :download-destination-index="chartIndex"
                        @download-link="(info) => addDownloadLink(info, chart)" />
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
          </v-table>
        </v-col>

        <v-col cols="12" class="d-flex mt-4">
          <v-btn
            :loading="showPreloader"
            color="primary"
            variant="outlined"
            class="mt-n4 mb-4"
            :disabled="!props.orderKey"
            @click="getSupportingDocumentsMergedPdf(props.orderKey)">
            View Supporting Documents
          </v-btn>
        </v-col>
      </v-row>

      <v-alert
        v-if="props.formButtonClicked && !supplementalDocsUploaded() && !props.patientChartNotNeeded"
        type="error"
        variant="tonal"
        density="compact"
        class="mb-4 form-validation-alert">
        Please upload supplemental documents before proceeding.
      </v-alert>
    </v-col>

    <v-col cols="12">
      <v-row v-if="props.isEditable" class="section-container">
        <v-col cols="12">
          <SupplementalDocumentsUploadComponent
            :is-editable="props.isEditable"
            :supplemental-docs="supplementalDocs"
            :cmm-budgeted="props.cmmBudgeted"
            :other-links="otherLinks"
            @add-free-form-supplemental-docs="addFreeFormSupplementalDocsDownloadLink" />
        </v-col>
      </v-row>
    </v-col>

    <v-col v-if="props.isEditable || (props.medicalNecessity && props.medicalNecessity.length)" cols="12">
      <v-row class="section-container">
        <v-col cols="12" class="header-with-select-box">
          <h3 class="text-primary weight-600">Statement of Medical Necessity</h3>
        </v-col>
        <v-col cols="12">
          <v-table class="">
            <thead>
              <tr>
                <th>Uploaded Document</th>
                <th class="text-right"></th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Statement of Medical Necessity</td>
                <td>
                  <div class="d-flex align-center justify-end flex-wrap ga-2">
                    <div v-if="medicalNecessity && medicalNecessity.length" class="text-right">
                      <div
                        v-for="link in medicalNecessity"
                        :key="link"
                        class="d-flex align-center justify-end flex-wrap">
                        <span v-if="captionFor(link)" class="text-caption text-medium-emphasis mr-2">
                          {{ captionFor(link) }}
                        </span>
                        <v-btn
                          v-if="props.isEditable"
                          variant="text"
                          color="outline"
                          prepend-icon="mdi-delete"
                          @click="deleteUploadedFile({ link: link, storeVar: `medicalNecessity` })">
                          Delete
                        </v-btn>
                        <v-btn
                          variant="text"
                          color="primary"
                          prepend-icon="mdi-download"
                          @click="openDocument(props.orderKey, link)">
                          Download
                        </v-btn>
                      </div>
                    </div>
                    <div v-else>
                      <div v-if="!props.isEditable" class="">Not Uploaded</div>
                      <div v-else>
                        <ChartUploadFileComponent
                          v-if="props.cmmBudgeted"
                          store-var="medicalNecessity"
                          :other-links="otherLinks"
                          @download-link="addDownloadLink" />
                        <UploadFileComponent
                          v-else
                          :download-destination="`medicalNecessity`"
                          @download-link="addDownloadLink" />
                      </div>
                    </div>
                    <v-btn
                      v-if="canGenerateLetter"
                      variant="text"
                      color="primary"
                      :prepend-icon="letterState.generating ? 'mdi-progress-clock' : 'mdi-file-document-edit-outline'"
                      :disabled="!props.orderKey"
                      @click="onGenerateLetter">
                      {{ letterButtonLabel }}
                    </v-btn>
                  </div>
                </td>
              </tr>
            </tbody>
          </v-table>
        </v-col>
      </v-row>
    </v-col>
    <MedicalNecessityLetterDialog
      v-if="canGenerateLetter"
      ref="letterDialogRef"
      v-model="letterDialogVisible"
      :order-key="props.orderKey"
      :initial-draft="props.medicalNecessityLetter?.draftText"
      @attached="attachGeneratedLetter"
      @state-changed="letterState = $event" />
  </v-row>
</template>

<script setup>
import { computed, ref, toRef } from "vue";
import ChartUploadFileComponent from "@/components/ChartUploadFileComponent.vue";
import UploadFileComponent from "@/components/UploadFileComponent";
import SupplementalDocumentsUploadComponent from "@/components/SupplementalDocumentsUploadComponent";
import MedicalNecessityLetterDialog from "@/components/MedicalNecessityLetterDialog.vue";
import { orderInfoService } from "@/services/orderInfoService";
import { usePreparedInfo } from "@/composables/usePreparedInfo";
import { useDocumentLink } from "@/composables/useDocumentLink";
import { useRequirementsStore } from "@/stores/requirementsStore";

const props = defineProps({
  patientChart: {
    type: Array,
    default() {
      return [];
    },
  },
  medicalNecessity: {
    type: Array,
    default() {
      return [];
    },
  },
  medicalNecessityTemplate: {
    type: String,
    default() {
      return "";
    },
  },
  medicalNecessityLetter: {
    type: Object,
    default: null,
  },
  benefitType: {
    type: String,
    default: "",
  },
  isEditable: {
    type: Boolean,
    default() {
      return false;
    },
  },
  supplementalDocs: {
    type: Array,
    default() {
      return [];
    },
  },
  orderKey: {
    type: String,
    default() {
      return "";
    },
  },
  formButtonClicked: {
    type: Boolean,
    default() {
      return false;
    },
  },
  patientChartNotNeeded: {
    type: Boolean,
    default() {
      return false;
    },
  },
  cmmBudgeted: {
    type: Boolean,
    default() {
      return false;
    },
  },
});

// toRef, not ref(props.x): the parent replaces the array when it merges these into the patient
// chart, and a captured copy would keep reading the old rows.
const supplementalDocs = toRef(props, "supplementalDocs");
const { openDocument } = useDocumentLink();

const otherLinks = computed(() => [
  ...(props.patientChart || []).flatMap((doc) => doc.links?.slice(0, 1) || []),
  ...supplementalDocs.value.flatMap((doc) => doc.links?.slice(0, 1) || []),
  ...(props.medicalNecessity || []),
]);
const { remember, captionFor } = usePreparedInfo();
const requirementsStore = useRequirementsStore();

const canGenerateLetter = computed(
  () =>
    props.isEditable &&
    props.benefitType === "medical_benefits" &&
    (requirementsStore.clientConfig?.priorAuth?.medicalNecessityLettersEnabled ?? false)
);

const letterDialogRef = ref(null);
const letterDialogVisible = ref(false);
const letterState = ref({ generating: false, hasDraft: !!props.medicalNecessityLetter?.draftText });

const letterButtonLabel = computed(() => {
  if (letterState.value.generating) return "Drafting letter…";
  return letterState.value.hasDraft ? "Edit generated letter" : "Generate letter";
});

const onGenerateLetter = async () => {
  if (letterState.value.hasDraft || letterState.value.generating) {
    letterDialogVisible.value = true;
    return;
  }
  await letterDialogRef.value?.startDraft();
};

const emit = defineEmits([
  "addUploadedFile",
  "updateGuidelines",
  "add-free-form-supplemental-docs",
  "deleteUploadedFile",
]);

const showPreloader = ref(false);

const addDownloadLink = (downloadLinkInfo, chartEntry) => {
  remember(downloadLinkInfo.link, downloadLinkInfo);
  emit("addUploadedFile", chartEntry ? { ...downloadLinkInfo, chartEntry } : downloadLinkInfo);
};

const deleteUploadedFile = (downloadLinkInfo) => {
  downloadLinkInfo.isDelete = true;
  emit("deleteUploadedFile", downloadLinkInfo);
};

const attachGeneratedLetter = ({ link, removedLinks }) => {
  for (const removed of removedLinks) {
    emit("deleteUploadedFile", { link: removed, storeVar: "medicalNecessity", isDelete: true });
  }
  emit("addUploadedFile", { link, storeVar: "medicalNecessity" });
};

const addFreeFormSupplementalDocsDownloadLink = (downloadLinkInfo) => {
  emit("add-free-form-supplemental-docs", downloadLinkInfo);
};

function supplementalDocsUploaded() {
  return (
    (props.patientChart &&
      props.patientChart.length > 0 &&
      props.patientChart.some((doc) => doc.links && doc.links.length > 0)) ||
    supplementalDocs.value.some((doc) => doc.links && doc.links.length > 0)
  );
}

function getSupportingDocumentsMergedPdf(orderKey) {
  showPreloader.value = true;
  orderInfoService.getSupportingDocumentsPdf(orderKey).then((response) => {
    const pdfBlob = new Blob([response.data], { type: "application/pdf" });
    const pdfUrl = URL.createObjectURL(pdfBlob);
    window.open(pdfUrl, "_blank");
    setTimeout(() => URL.revokeObjectURL(pdfUrl), 10000);
    showPreloader.value = false;
  });
}
</script>

<style lang="scss" scoped>
@import "../styles/pages/_patient-information-form.scss";
</style>
